import {
  BASE_MIDI_NOTE,
  LOOP_LENGTH_BEATS,
  MAX_EVENTS_PER_LAYER,
  NOTE_NAMES,
  QUANTIZATION_STEP,
  SCALE_LANES,
  VIEWBOX_HEIGHT,
  VIEWBOX_WIDTH,
} from './constants';

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const midiToFrequency = (midi) => 440 * 2 ** ((midi - 69) / 12);

export const midiToLabel = (midi) => {
  const name = NOTE_NAMES[midi % 12];
  const octave = Math.floor(midi / 12) - 1;
  return `${name}${octave}`;
};

export const buildPath = (points) => {
  if (!points.length) {
    return '';
  }

  const scaled = points.map((point) => ({
    x: point.x * VIEWBOX_WIDTH,
    y: point.y * VIEWBOX_HEIGHT,
  }));

  if (scaled.length === 1) {
    const { x, y } = scaled[0];
    const DOT_PX = 6;
    return `M ${x} ${y} L ${x + DOT_PX} ${y + DOT_PX}`;
  }

  const pathCommands = [`M ${scaled[0].x} ${scaled[0].y}`];

  for (let index = 1; index < scaled.length; index += 1) {
    const current = scaled[index];
    const previous = scaled[index - 1];
    const midX = (previous.x + current.x) / 2;
    const midY = (previous.y + current.y) / 2;
    pathCommands.push(`Q ${previous.x} ${previous.y} ${midX} ${midY}`);
  }

  const last = scaled[scaled.length - 1];
  pathCommands.push(`L ${last.x} ${last.y}`);

  return pathCommands.join(' ');
};

export const getLaneFromPoint = (point) => {
  const laneWidth = 1 / SCALE_LANES;
  const lane = Math.floor(point.x / laneWidth);
  return clamp(lane, 0, SCALE_LANES - 1);
};

export const quantizeBeat = (beat) => {
  const rounded = Math.round(beat / QUANTIZATION_STEP) * QUANTIZATION_STEP;
  const wrapped = ((rounded % LOOP_LENGTH_BEATS) + LOOP_LENGTH_BEATS) % LOOP_LENGTH_BEATS;
  return Number(wrapped.toFixed(3));
};

export const createEmptyLayer = (index) => ({
  id: `layer-${index + 1}`,
  name: `Layer ${index + 1}`,
  strokes: [],
  events: [],
  muted: false,
  solo: false,
  densityReached: false,
});

export const canLayerAcceptEvents = (layer, incomingCount = 0) =>
  layer.events.length + incomingCount <= MAX_EVENTS_PER_LAYER;

export const getLayerColor = (layer, fallbackColor, instruments) => {
  if (!layer.strokes.length) {
    return fallbackColor;
  }
  const brushId = layer.strokes[0]?.brushId;
  const brush = instruments.find((entry) => entry.id === brushId);
  return brush?.color ?? fallbackColor;
};

export const normalizeVelocity = (value) => clamp(value, 0.05, 1);

export const toFrequencyFromLane = (lane, scaleSteps) => {
  const step = scaleSteps[clamp(lane, 0, scaleSteps.length - 1)];
  const midi = BASE_MIDI_NOTE + step;
  return {
    midi,
    frequency: midiToFrequency(midi),
  };
};